import axios from 'axios'
import store from '@/store'
import router from '@/router'
import Swal from 'sweetalert2'

class ResponseError extends Error {
  constructor (data) {
    super(data.message)
    this.message = data

    this.name = 'ResponseError'
  }
}

axios.defaults.baseURL = process.env.VUE_APP_API_URL
axios.defaults.timeout = 15000

let isHandlingAuthFailure = false

const getForcePasswordChangeRouteName = () => {
  return store.getters['auth/userType'] === 'student'
    ? 'student.force-password-change'
    : 'shared.force-password-change'
}

const isPasswordChangeRequiredError = error => {
  return error?.response?.status === 403 && error?.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED'
}

// Request interceptor
axios.interceptors.request.use(config => {
  const token = store.getters['auth/token']
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor
axios.interceptors.response.use(
  response => Promise.resolve(response),
  async error => {
    if (isPasswordChangeRequiredError(error)) {
      const forceRouteName = getForcePasswordChangeRouteName()

      if (router.currentRoute?.name !== forceRouteName) {
        router.replace({ name: forceRouteName })
      }

      return Promise.reject(new ResponseError(error?.response?.data || { message: 'Password change required.' }))
    }

    const status = error?.response?.status

    if ([401, 403].includes(status)) {
      if (!isHandlingAuthFailure) {
        isHandlingAuthFailure = true

        if (store.getters['auth/check']) {
          store.dispatch('auth/logout')
        }

        if (router.currentRoute?.name !== 'shared.login') {
          router.replace({ name: 'shared.login' })
        }

        setTimeout(() => {
          isHandlingAuthFailure = false
        }, 1000)
      }

      return Promise.reject(new ResponseError(error?.response?.data || { message: 'Unauthorized' }))
    }

    let message = {
      title: 'Error Connection',
      text: 'Please check your internet connection'
    }

    // let requestError = new AxiosError(error?.message, 500)

    if (error.response && error.response.data) {
      const { data, status } = error.response // , statusText

      // requestError = new AxiosError(
      //   JSON.stringify(error.response.data),
      //   error.response.status,
      //   error.response.config,
      //   error.response.request,
      //   error.response
      // )

      switch (status) {
        case 400: { // bad request
          message = {
            title: 'Oops!', // 'Invalid Credentials / Payload',
            text: data.message
          }
          break
        }
        case 401: {
          message = {
            title: 'Session Expired', // 'Session Expired',
            text: 'Due to inactivity, please log in again'
          }
          break
        }
        case 403: {
          message = {
            title: 'Forbidden Page', // 'Unauthorize Access',
            text: 'You are attempting to access a restricted page.'
          }
          break
        }
        case 404: {
          message = {
            title: 'Page Not Found',
            text: 'You are attempting to access a unknown page.'
          }
          break
        }
        case 413: {
          message = {
            title: 'Attachment Too Large',
            text: 'Please limit the size of your files.'
          }
          break
        }
        case 422: { // unprocessable
          message = {
            title: 'Validation Error', // 'Invalid Data Format',
            text: 'Your inputs seem to be incorrect or incomplete, please check and try again.'
          }
          break
        }
        case 429: { // rate limit
          const match = data.message && data.message.match(/(\d+)/)
          const retryAfter = match ? parseInt(match[1]) : 0
          message = {
            title: 'Too Many Requests',
            text: data.message,
            retryAfter: retryAfter
          }
          break
        }
        default: {
          message = {
            title: 'Oops!', // 'Server Error'
            text: 'Something went wrong, please contact your System Administrator'
          }
        }
      }
    } else {
      if (navigator.onLine) {
        message = {
          title: 'Oops!', // 'Server Error'
          text: 'Something went wrong, please contact your System Administrator'
        }
      }
    }

    if (Swal.isVisible()) {
      return Promise.reject(new ResponseError(error?.response?.data || { message: message.text }))
    }

    return Swal.fire({
      title: message.title,
      html: `<h6>${message.retryAfter ? message.text.replace(/\d+/, message.retryAfter) : message.text}</h6>`,
      allowEscapeKey: false,
      allowOutsideClick: false,
      focusConfirm: true,
      showConfirmButton: !message.retryAfter,
      confirmButtonText: 'Dismiss',
      customClass: {
        title: 'h4 p-2 text-danger border-bottom',
        htmlContainer: 'p-2 m-0',
        actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
        confirmButton: 'btn btn-sm btn-danger'
      },
      width: '25rem',
      didOpen: modal => {
        if (message.retryAfter) {
          let countdown = message.retryAfter
          const htmlContainer = modal.querySelector('.swal2-html-container h6')
          const zeroText = message.text.replace(/\d+/, 0)

          const updateCountdown = () => {
            if (countdown > 0) {
              countdown--
              if (htmlContainer) {
                htmlContainer.innerHTML = message.text.replace(/\d+/, countdown)
              }
              setTimeout(updateCountdown, 1000)
            } else {
              Swal.update({
                showConfirmButton: true,
                html: `<h6>${zeroText}</h6>`
              })
            }
          }

          setTimeout(updateCountdown, 1000)
        }
      }
    }).then(() => {
      return Promise.reject(new ResponseError(error?.response?.data || { message: message.text }))
    })
  }
)
